import { styled } from '@linaria/react';
import { Loader, Stack } from '@mantine/core';
import { Fragment, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGoalWorkChartData, useOutcomeWorkChartData } from '../../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { secondaryBase, skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip as InfoTooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { CHART_COLORS } from './color-helpers';

type WorkCommittedCompletedChartProps = {
  outcomeId?: string | undefined;
  goalId?: string | undefined;
};

// Generate a consistent color for a project name using a simple hash
const getProjectColor = (projectName: string): string => {
  let hash = 0;
  for (let i = 0; i < projectName.length; i++) {
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CHART_COLORS[Math.abs(hash) % CHART_COLORS.length];
};

export function WorkCommittedCompletedChart({ outcomeId, goalId }: WorkCommittedCompletedChartProps) {
  const i18n = useTranslation();
  // Fetch aggregated work chart data
  const { data: outcomeChartData, query: outcomeQuery } = useOutcomeWorkChartData(outcomeId, 6);
  const { data: goalChartData, query: goalQuery } = useGoalWorkChartData(goalId, 6);

  const chartDataResponse = outcomeId ? outcomeChartData : goalChartData;
  const query = outcomeId ? outcomeQuery : goalQuery;

  const { chartData, projectNames, projectColorMap, projects } = useMemo(() => {
    if (!chartDataResponse || !chartDataResponse.months || !chartDataResponse.projects) {
      return { chartData: [], projectNames: [], projectColorMap: {}, projects: [] };
    }

    const projectNamesMap = chartDataResponse.project_names_map || {};
    const sortedProjectNames: string[] = [];
    const colorMap: Record<string, string> = {};

    // Build project names and color map
    chartDataResponse.projects.forEach((project: any) => {
      const projectName = projectNamesMap[project.id] || project.name;
      sortedProjectNames.push(projectName);
      colorMap[projectName] = getProjectColor(projectName);
    });

    // Transform API response into chart format
    const data = chartDataResponse.months.map((month: string) => {
      const dataPoint: Record<string, string | number> = { month };

      chartDataResponse.projects.forEach((project: any) => {
        const projectName = projectNamesMap[project.id] || project.name;
        const committed = project.committed[month] || 0;
        const completed = project.completed[month] || 0;

        dataPoint[`${projectName}_committed`] = committed;
        dataPoint[`${projectName}_completed`] = completed;
      });

      return dataPoint;
    });

    return {
      chartData: data,
      projectNames: sortedProjectNames,
      projectColorMap: colorMap,
      projects: chartDataResponse.projects,
    };
  }, [chartDataResponse]);

  if (!outcomeId && !goalId) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="analytics" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.work_committed_completed')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_work_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  if (query.isLoading) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="analytics" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.work_committed_completed')}</SectionTitle>
            <Loader size="sm" variant="dots" color={secondaryBase} />
          </SectionHeader>
          <ChartContainer />
        </Stack>
      </SectionCard>
    );
  }

  if (chartData.length === 0) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="analytics" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.work_committed_completed')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_work_data')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <Stack gap={12}>
        <SectionHeader>
          <Icon name="analytics" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
          <SectionTitle>{i18n.t('strategy.overview.sections.work_committed_completed')}</SectionTitle>
          <InfoTooltip
            variant="black"
            label={i18n.t('strategy.overview.tooltips.work_committed_completed', {
              work_units: i18n.t('common.work_units'),
            })}
            zIndex={1000}
          >
            <Icon name="info" size={16} color={skyDark} style={{ cursor: 'help' }} />
          </InfoTooltip>
        </SectionHeader>

        <ChartContainer>
          <ResponsiveContainer width="100%" height={430}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
              <XAxis
                dataKey="month"
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.month'),
                  position: 'insideBottom',
                  offset: -10,
                  style: { fontSize: '12px', fill: skyDark },
                }}
                height={90}
              />
              <YAxis
                stroke={skyDark}
                style={{ fontSize: '12px', fontFamily: 'Figtree' }}
                label={{
                  value: i18n.t('strategy.overview.charts.work_units', { work_units: i18n.t('common.work_units') }),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: '12px', fill: skyDark },
                }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  const month = payload[0].payload.month;
                  const projectNamesMap = chartDataResponse?.project_names_map || {};

                  return (
                    <div
                      style={{
                        backgroundColor: 'white',
                        border: `1px solid ${skyLight}`,
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '12px',
                        fontFamily: 'Figtree',
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{month}</div>
                      {payload.map((entry: any) => {
                        const isCommitted = entry.dataKey.endsWith('_committed');
                        const projectNameFromKey = entry.dataKey.replace('_committed', '').replace('_completed', '');
                        const project = projects.find((p: any) => {
                          const pName = projectNamesMap[p.id] || p.name;
                          return pName === projectNameFromKey;
                        });
                        const isOthers = project?.id === 'others';
                        const type = isCommitted ? 'Committed' : 'Completed';

                        return (
                          <div key={entry.dataKey} style={{ marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: entry.fill,
                                  borderRadius: '2px',
                                  opacity: isCommitted ? 1 : 0.6,
                                }}
                              />
                              <span style={{ fontWeight: 'medium' }}>
                                {projectNameFromKey} ({type}):
                              </span>
                              <span style={{ fontWeight: 'bold' }}>{entry.value} tasks</span>
                            </div>
                            {isOthers &&
                              isCommitted &&
                              project.included_initiatives &&
                              project.included_initiatives.length > 0 && (
                                <div style={{ marginLeft: '20px', marginTop: '4px', fontSize: '11px', color: skyDark }}>
                                  <div style={{ fontStyle: 'italic', marginBottom: '2px' }}>
                                    Aggregated from initiatives:
                                  </div>
                                  {project.included_initiatives.map((inc: any) => (
                                    <div key={inc.id}>• {inc.name}</div>
                                  ))}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', fontFamily: 'Figtree', paddingTop: '30px' }}
                formatter={(value: string) => {
                  const isCommitted = value.endsWith('_committed');
                  const projectName = value.replace('_committed', '').replace('_completed', '');
                  const type = isCommitted ? 'Committed' : 'Completed';
                  return `${projectName} (${type})`;
                }}
                iconType="square"
              />
              {projectNames.map((projectName) => (
                <Fragment key={projectName}>
                  <Bar dataKey={`${projectName}_committed`} stackId="committed" fill={projectColorMap[projectName]} />
                  <Bar
                    dataKey={`${projectName}_completed`}
                    stackId="completed"
                    fill={projectColorMap[projectName]}
                    fillOpacity={0.6}
                  />
                </Fragment>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </Stack>
    </SectionCard>
  );
}

const SectionCard = styled.div`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${skyLight};
  flex: 1;
  min-height: 350px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;

const SectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  text-align: left;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 430px;
  margin-top: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px dashed ${skyLight};
`;
